import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Row, Col, Card, Alert, Spinner, Collapse, Tab, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom'; // Import Link
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

ChartJS.register(ArcElement, Tooltip, Legend);

function Navigator() {
  const [jobDescription, setJobDescription] = useState('');
  
  const [resumeFile, setResumeFile] = useState(null);
  const [matchScore, setMatchScore] = useState(null);
  const [missingKeywords, setMissingKeywords] = useState({});
  const [extractedResumeText, setExtractedResumeText] = useState('');
  const [foundKeywords, setFoundKeywords] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendStatus, setBackendStatus] = useState('checking');
  const [showPathfinderCTA, setShowPathfinderCTA] = useState(false); // New state for CTA
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  const [openSections, setOpenSections] = useState({
    minimum_requirements: false,
    preferred_requirements: false,
    responsibilities: false,
    general: false,
    found_minimum_requirements: false,
    found_preferred_requirements: false,
    found_responsibilities: false,
    found_general: false,
    howToImprove: false,
  });
  const [activeTab, setActiveTab] = useState('jobDescription');
  const resumeFileInputRef = useRef(null);

  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/`);
        if (response.ok) {
          setBackendStatus('online');
        } else {
          setBackendStatus('offline');
        } 
      } catch (err) {
        setBackendStatus('offline');
      }
    };

    checkBackendStatus();
    const interval = setInterval(checkBackendStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleJobDescriptionChange = async (e) => {
    const value = e.target.value;
    setJobDescription(value);

    // URL detection logic
    try {
      const url = new URL(value);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        setIsFetchingUrl(true);
        setError(null);
        const formData = new FormData();
        formData.append('url', value);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/fetch-url-content/`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch content from the URL.' }));
          throw new Error(errorData.detail || 'Could not parse error response from server.');
        }

        const data = await response.json();
        setJobDescription(data.extracted_text);
      }
    } catch (error) {
      // Not a valid URL, do nothing
      if (error instanceof TypeError) { 
        // This is expected if the pasted text is not a URL.
      } else {
        setError('Could not fetch content from the provided URL. Please paste the text directly.');
      }
    } finally {
      setIsFetchingUrl(false);
    }
  };

  const handleFileChange = (event) => {
    setResumeFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setMatchScore(null);
    setMissingKeywords({});
    setExtractedResumeText('');
    setFoundKeywords({});
    setShowPathfinderCTA(false); // Reset CTA on new submission

    if (!jobDescription || !resumeFile) {
      setError('Please provide both a job description and a resume file.');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('job_description', jobDescription);
    formData.append('resume_file', resumeFile);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/analyze-resume/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'An error occurred during analysis.');
      }

      const data = await response.json();
      setMatchScore(data.match_score);
      setMissingKeywords(data.missing_keywords || {});
      setExtractedResumeText(data.extracted_resume_text || '');
      setFoundKeywords(data.found_keywords || {});
      

      // Trigger for the Pathfinder CTA
      if (data.match_score < 70) {
        setShowPathfinderCTA(true);
      } else {
        setShowPathfinderCTA(false);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setJobDescription('');
    setResumeFile(null);
    setMatchScore(null);
    setMissingKeywords({});
    setExtractedResumeText('');
    setFoundKeywords({});
    setLoading(false);
    setError(null);
    setShowPathfinderCTA(false); // Reset CTA on clear
    if (resumeFileInputRef.current) {
      resumeFileInputRef.current.value = "";
    }
  };

  const handlePathfinderHandoff = () => {
    try {
      // Extract the job title from the first line of the job description
      const title = jobDescription.split('\n')[0].trim();
      const handoffData = {
        destination: title,
        gaps: missingKeywords
      };
      sessionStorage.setItem('navigatorHandoffData', JSON.stringify(handoffData));
    } catch (e) {
      console.error("Error preparing handoff data:", e);
    }
  };


  const getBackendStatusColor = () => {
    if (backendStatus === 'online') return 'green';
    if (backendStatus === 'offline') return 'red';
    return 'gray';
  };

  const getGlassEffectStyle = (color) => {
    return {
      height: '16px',
      width: '16px',
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: '8px',
      verticalAlign: 'middle',
      backgroundColor: color,
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
    };
  };

  const highlightText = (text, keywordsDict) => {
    if (!text || !keywordsDict) {
      return text;
    }

    const keywordMap = new Map();
    for (const section in keywordsDict) {
      if (Array.isArray(keywordsDict[section])) {
        keywordsDict[section].forEach(keyword => {
          if (typeof keyword === 'string') {
            keywordMap.set(keyword.toLowerCase(), section);
          }
        });
      }
    }

    const sortedKeywords = [...keywordMap.keys()].sort((a, b) => b.length - a.length);

    if (sortedKeywords.length === 0) {
      return text;
    }

    const regex = new RegExp(`(${sortedKeywords.map(kw => kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');

    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (!part) return null;
      const lowerCasePart = part.toLowerCase();
      if (keywordMap.has(lowerCasePart)) {
        const section = keywordMap.get(lowerCasePart);
        let highlightClass = '';
        switch (section) {
          case 'minimum_requirements':
            highlightClass = 'highlight-minimum';
            break;
          case 'preferred_requirements':
            highlightClass = 'highlight-preferred';
            break;
          case 'responsibilities':
            highlightClass = 'highlight-responsibilities';
            break;
          case 'general':
            highlightClass = 'highlight-general';
            break;
          default:
            break;
        }
        return (
          <span key={index} className={`highlight-beveled ${highlightClass}`}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const highlightGaps = (text, keywordsDict) => {
    if (!text || !keywordsDict) {
      return text;
    }

    const keywordMap = new Map();
    for (const section in keywordsDict) {
      if (Array.isArray(keywordsDict[section])) {
        keywordsDict[section].forEach(keyword => {
          if (typeof keyword === 'string') {
            keywordMap.set(keyword.toLowerCase(), section);
          }
        });
      }
    }

    const sortedKeywords = [...keywordMap.keys()].sort((a, b) => b.length - a.length);

    if (sortedKeywords.length === 0) {
      return text;
    }

    const regex = new RegExp(`(${sortedKeywords.map(kw => kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');

    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (!part) return null;
      const lowerCasePart = part.toLowerCase();
      if (keywordMap.has(lowerCasePart)) {
        const section = keywordMap.get(lowerCasePart);
        let highlightClass = '';
        switch (section) {
          case 'minimum_requirements':
            highlightClass = 'highlight-gap-minimum';
            break;
          case 'preferred_requirements':
            highlightClass = 'highlight-gap-preferred';
            break;
          case 'responsibilities':
            highlightClass = 'highlight-gap-responsibilities';
            break;
          case 'general':
            highlightClass = 'highlight-gap-general';
            break;
          default:
            break;
        }
        return (
          <span key={index} className={`highlight-beveled ${highlightClass}`}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const formatSectionName = (section) => {
    return section.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const interpretScore = (score) => {
    if (score >= 80) return "Excellent Match ⭐⭐⭐⭐⭐";
    if (score >= 60) return "Good Match ⭐⭐⭐⭐";
    if (score >= 40) return "Fair Match ⭐⭐⭐";
    return "Poor Match ⭐⭐";
  };

  const getChartColors = (score) => {
    if (score >= 80) return ['#4CAF50', '#E0E0E0'];
    if (score >= 60) return ['#64B5F6', '#E0E0E0'];
    if (score >= 40) return ['#FFC107', '#E0E0E0'];
    return ['#A2A2A2', '#E0E0E0'];
  };

  const chartData = {
    labels: ['Match', 'Mismatch'],
    datasets: [
      {
        data: matchScore !== null ? [matchScore, 100 - matchScore] : [0, 100],
        backgroundColor: getChartColors(matchScore),
        borderColor: getChartColors(matchScore),
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      tooltip: {
        position: 'nearest',
        backgroundColor: '#333333',
        titleColor: 'rgba(255, 255, 255, 1)',
        bodyColor: 'rgba(255, 255, 255, 1)',
        padding: 10,
        cornerRadius: 6,
        caretPadding: 15,
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += context.parsed.toFixed(2) + '%';
            }
            return label;
          }
        }
      },
      legend: {
        display: false,
      },
    },
  };

  const extractedResumeTextCard = extractedResumeText && (
    <Card className="p-4 h-100 card-coastal">
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="jobDescription">Job Description</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="resume">Extracted Resume Text</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="jobDescription">
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', padding: '10px', backgroundColor: '#f8f8f8', borderRadius: '5px' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {highlightGaps(jobDescription, missingKeywords)}
              </pre>
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="resume">
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', padding: '10px', backgroundColor: '#f8f8f8', borderRadius: '5px' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {highlightText(extractedResumeText, foundKeywords)}
              </pre>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Card>
  );

  const getBadgeClass = (section) => {
    switch (section) {
      case 'minimum_requirements':
        return 'count-badge-minimum';
      case 'preferred_requirements':
        return 'count-badge-preferred';
      case 'responsibilities':
        return 'count-badge-responsibilities';
      case 'general':
        return 'count-badge-general';
      default:
        return 'count-badge-general';
    }
  };

  const getGapBadgeClass = (section) => {
    switch (section) {
      case 'minimum_requirements':
        return 'gap-badge-minimum';
      case 'preferred_requirements':
        return 'gap-badge-preferred';
      case 'responsibilities':
        return 'gap-badge-responsibilities';
      case 'general':
        return 'gap-badge-general';
      default:
        return 'gap-badge-general';
    }
  };

  const foundKeywordsCard = Object.keys(foundKeywords).length > 0 && (
    <Card className="p-4 h-100 card-coastal">
      <h2 className="text-center mb-3">Target Qualifications Confirmed</h2>
      {Object.entries(foundKeywords).map(([section, keywords]) => (
        <div key={section} className="mb-2">
          <Button
            onClick={() => toggleSection(`found_${section}`)}
            aria-controls={`collapse-found-${section}`}
            aria-expanded={openSections[`found_${section}`]}
            variant="link"
            className="text-decoration-none p-0"
          >
            <h6>
              {formatSectionName(section)}{' '}
              <span className={`count-badge ${getBadgeClass(section)}`}>
                ({keywords.length})
              </span>{' '}
              <i className={`bi bi-chevron-${openSections[`found_${section}`] ? 'up' : 'down'}`}></i>
            </h6>
          </Button>
          <Collapse in={openSections[`found_${section}`]}>
            <div id={`collapse-found-${section}`}>
              {keywords.length > 0 ? (
                <ul style={{ listStyleType: 'none', paddingLeft: '0' }}>
                  {keywords.map((keyword, index) => (
                    <li key={index}>{keyword}</li>
                  ))}
                </ul>
              ) : (
                <p>No keywords found in this section.</p>
              )}
            </div>
          </Collapse>
        </div>
      ))}
    </Card>
  );

  return (
    <div className="App">
      <Container className="my-5">
        <img src="/lonesailorlogo.png" alt="Lone Sailor Logo" className="mb-4" style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '150px' }} />
        <h1 className="app-title text-center mb-2">The Navigator</h1>
        <p className="app-tagline text-center mb-5">See your value. Bridge the gap. Chart your career.</p>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              <Form.Group className="h-100 d-flex flex-column" controlId="jobDescription">
                <Form.Label>Job Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  placeholder="Every voyage needs a destination. Paste the job description here (or link) to set your coordinates. Then, upload your resume, and our AI Navigator will plot the course."
                  value={jobDescription}
                  onChange={handleJobDescriptionChange}
                  required
                  className="shadow-sm flex-grow-1"
                />
                {isFetchingUrl && <Spinner animation="border" size="sm" className="mt-2" />}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="h-100 d-flex flex-column" controlId="resumeUpload">
                <Form.Label>Upload Resume (.docx or .pdf)</Form.Label>
                <Form.Control
                  type="file"
                  accept=".docx,.pdf"
                  onChange={handleFileChange}
                  required
                  className="shadow-sm"
                  ref={resumeFileInputRef}
                />
                <Form.Text className="text-muted small">
                  By uploading your resume, you agree to our <a href="/legal" target="_blank">Terms of Service and Privacy Policy</a>.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-grid gap-2 mt-3">
            <Button variant="primary" type="submit" size="lg" disabled={loading || backendStatus !== 'online'} className="btn-primary-custom">
              {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Chart My Course'}
            </Button>
            
            <Button variant="secondary" size="lg" onClick={handleClear} className="btn-clear-deck mt-2">
              Clear the Deck
            </Button>
          </div>
        </Form>

        {error && <Alert variant="danger" className="mt-4">Error: {error}</Alert>}

        {matchScore !== null && (
            <Alert variant="light" className="mt-4 border small text-center">
              <i className="bi bi-compass me-2" style={{ color: '#D4AF37' }}></i>
              <strong>AI Guidance:</strong> AI-generated analysis. Please review carefully. Use of this service is subject to our <a href="/legal" target="_blank" className="alert-link">Terms of Service and Privacy Policy</a>.
            </Alert>
        )}

        {matchScore !== null && (
          <Row className="mt-4">
            <Col xs={12} md={6} className="mb-4 mb-md-0">
              <Card className="p-4 h-100 card-coastal">
                <h2 className="text-center mb-3">Position Report</h2>
                <div className="d-flex justify-content-center mb-3" style={{ height: '200px', position: 'relative' }}>
                  <Doughnut data={chartData} options={chartOptions} />
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: '#333',
                    pointerEvents: 'none'
                  }}>
                    {typeof matchScore === 'number' ? `${matchScore.toFixed(2)}%` : '0.00%'}
                  </div>
                </div>
                
                <p className="text-center fs-4 fw-bold mt-3">{interpretScore(matchScore)}</p>

                <div className="mt-4">
                  <h5 className="mb-3">Scorecard:</h5>
                  <ul className="list-unstyled">
                    <li className="d-flex align-items-center mb-2">
                      <span style={{ ...getGlassEffectStyle('#4CAF50'), marginRight: '10px' }}></span>
                      <span>Excellent Match ⭐⭐⭐⭐⭐ (80% - 100%)</span>
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <span style={{ ...getGlassEffectStyle('#64B5F6'), marginRight: '10px' }}></span>
                      <span>Good Match ⭐⭐⭐⭐ (60% - 79%)</span>
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <span style={{ ...getGlassEffectStyle('#FFC107'), marginRight: '10px' }}></span>
                      <span>Fair Match ⭐⭐⭐ (40% - 59%)</span>
                    </li>
                    <li className="d-flex align-items-center">
                      <span style={{ ...getGlassEffectStyle('#A2A2A2'), marginRight: '10px' }}></span>
                      <span>Poor Match ⭐⭐ (0% - 39%)</span>
                    </li>
                  </ul>
                </div>
                
                {Object.keys(missingKeywords).length > 0 && (
                  <div className="mt-4">
                    <h5>Target Qualifications Required:</h5>
                    {Object.entries(missingKeywords).map(([section, keywords]) => (
                      <div key={section} className="mb-2">
                        <Button
                          onClick={() => toggleSection(section)}
                          aria-controls={`collapse-${section}`}
                          aria-expanded={openSections[section]}
                          variant="link"
                          className="text-decoration-none p-0"
                        >
                          <h6>
                            {formatSectionName(section)}{' '}
                            <span className={`count-badge ${getGapBadgeClass(section)}`}>
                                ({keywords.length})
                            </span>{' '}
                            <i className={`bi bi-chevron-${openSections[section] ? 'up' : 'down'}`}></i>
                          </h6>
                        </Button>
                        <Collapse in={openSections[section]}>
                          <div id={`collapse-${section}`}>
                            {keywords.length > 0 ? (
                              <ul style={{ listStyleType: 'none', paddingLeft: '0' }}>
                                {keywords.map((keyword, index) => (
                                  <li key={index}>{keyword}</li>
                                ))}
                              </ul>
                            ) : (
                              <p>No significant missing keywords found in this section. Great job!</p>
                            )}
                          </div>
                        </Collapse>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={12} md={6}>
              {foundKeywordsCard}
            </Col>
          </Row>
        )}

        {showPathfinderCTA && (
          <Row className="mt-4">
            <Col>
              <Card className="p-4 text-center card-coastal-cta">
                <Card.Body>
                  <Card.Title as="h4">You Have Gaps in Your Readiness</Card.Title>
                  <Card.Text>
                    Don't worry, this is a normal part of the journey. Let's build a proactive plan to close these gaps and make you a competitive candidate.
                  </Card.Text>
                  <Button 
                    as={Link} 
                    to={`/pathfinder`}
                    variant="primary"
                    size="lg"
                    onClick={handlePathfinderHandoff}
                    className="btn-primary-custom"
                  >
                    Take Me to the Pathfinder
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {matchScore !== null && (
          <Row className="mt-4">
            <Col md={12}>
              {extractedResumeTextCard}
            </Col>
          </Row>
        )}

        {matchScore !== null && (
          <Row className="mt-4">
            <Col md={12}>
              <Card className="p-4 h-100 card-coastal">
                <Button
                  onClick={() => toggleSection('howToImprove')}
                  aria-controls="collapse-how-to-improve"
                  aria-expanded={openSections.howToImprove}
                  variant="link"
                  className="text-decoration-none p-0 text-center"
                >
                  <h5 className="mb-2">Your Next Waypoint <i className={`bi bi-chevron-${openSections.howToImprove ? 'up' : 'down'}`}></i></h5>
                </Button>
                <Collapse in={openSections.howToImprove}>
                  <div id="collapse-how-to-improve">
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item"><strong>Plot Your Victories:</strong> Use numbers to show the scale of your impact (e.g., "Increased efficiency by 15%").</li>
                      <li className="list-group-item"><strong>Man the Helm with Action Verbs:</strong> Lead with strong verbs like 'Engineered,' 'Accelerated,' and 'Led.'</li>
                      <li className="list-group-item"><strong>Set Your Course with a Custom Summary:</strong> Align your introduction directly with the mission of the role.</li>
                      <li className="list-group-item"><strong>Ensure a Clear Chart:</strong> Your resume must be easy for any captain to read at a glance.</li>
                      <li className="list-group-item"><strong>Inspect for Leaks:</strong> Eliminate all typos and grammatical errors before setting sail.</li>
                    </ul>
                  </div>
                </Collapse>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="mt-4">
          <Col>
            <Card className="p-3 card-coastal">
              <h5 className="text-center mb-2">System Status</h5>
              <div className="text-center">
                <span style={getGlassEffectStyle('green')}></span>
                <span style={{ verticalAlign: 'middle', marginRight: '20px' }}>Bridge</span>
                <span style={getGlassEffectStyle(getBackendStatusColor())}></span>
                <span style={{ verticalAlign: 'middle' }}>Engine Room</span>
              </div>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
}

export default Navigator;
